var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "../../context/Router";
import { useAuth } from "../../context/Auth";
import { MovieStatusEnum } from "../../types/Movie";
import { getEpisode, getMovie } from "../../services/player.service";
import { generateSource, getLastPlayed } from "./PlayerService";
import { Player } from "./Player";
import { PlayerStateProvider } from "../../context/PlayerStateContext";
import { LoadingSpinner } from "../../components/views/loading-spinner/LoadingSpinner";
import { focusManager } from "../../utils/Focus";
import { PlayerErrorMessageProvider, usePlayerErrorMessageActions, usePlayerErrorMessageData, } from "../../context/PlayerErrorMessageContext";
import { useTranslation } from "react-i18next";
import { InfoModal } from "../../components/views/modals/InfoModal";
import { getFirstPlayableEpisode, hasPlayableEpisodes, isEpisodePlayable, } from "../../utils/Helpers";
import { loadShaka } from "../../utils/LoadShaka";
export var WatchScreenInner = function () {
    var t = useTranslation().t;
    var _a = useRouter(), params = _a.params, navigate = _a.navigate, goBack = _a.goBack;
    var profile = useAuth().profile;
    var _b = useState(null), watchData = _b[0], setWatchData = _b[1];
    var _c = useState(true), loading = _c[0], setLoading = _c[1];
    var _d = useState(false), shakaReady = _d[0], setShakaReady = _d[1];
    var lastInputAtRef = useRef(Date.now());
    var errorActions = usePlayerErrorMessageActions();
    var errorState = usePlayerErrorMessageData();
    var showError = function (message, route, routeLabel) {
        setLoading(false);
        focusManager.pushLayer({ groupName: "modalView", index: 0 });
        errorActions.show(message, "text-danger", null);
        errorActions.returnTo(route, routeLabel);
    };
    // Shaka is fetched at runtime rather than bundled, so it has to arrive before
    // Player mounts. Starting it here runs it alongside the movie and episode
    // requests, which take far longer, so the whole cost hides behind the spinner
    // that is already on screen.
    useEffect(function () {
        var cancelled = false;
        loadShaka()
            .then(function () {
            if (!cancelled) {
                setShakaReady(true);
            }
        })
            .catch(function (error) {
            console.error("Failed to load the video player:", error);
            if (!cancelled) {
                showError(t("player.playback_failed"), "/home", t("layout.back_home"));
            }
        });
        return function () {
            cancelled = true;
        };
    }, []);
    useEffect(function () {
        var handleKeyDown = function () {
            lastInputAtRef.current = Date.now();
        };
        window.addEventListener("keydown", handleKeyDown);
        return function () { return window.removeEventListener("keydown", handleKeyDown); };
    }, []);
    useEffect(function () {
        if (!params.slug) {
            return;
        }
        setLoading(true);
        var watchHistory = {
            language: null,
            audio: null,
            caption: null,
            progress: 0,
            quality: "HD",
            currentTime: 0,
        };
        var currentEpisode;
        if (params.episode) {
            currentEpisode = params.episode;
        }
        var currentQuality;
        if (params.quality) {
            currentQuality = params.quality;
        }
        getMovie(params.slug)
            .then(function (movie) {
            var _a;
            if (!movie) {
                showError(t("player.episode_not_found"), "/home", t("layout.back_home"));
                return;
            }
            var episodeId = currentEpisode;
            var lastWatchedSeason = null;
            var lastWatchedEpisode = getLastPlayed(movie.episodes);
            if (lastWatchedEpisode) {
                if (lastWatchedEpisode.watchHistory) {
                    watchHistory = __assign(__assign({}, watchHistory), { language: lastWatchedEpisode.watchHistory.language, caption: lastWatchedEpisode.watchHistory.caption, quality: lastWatchedEpisode.watchHistory.quality });
                }
            }
            if (!episodeId) {
                if (lastWatchedEpisode && isEpisodePlayable(lastWatchedEpisode)) {
                    episodeId = lastWatchedEpisode.id;
                    lastWatchedSeason = lastWatchedEpisode.seasonNumber;
                }
                else {
                    var firstPlayable = getFirstPlayableEpisode(movie.episodes);
                    if (firstPlayable) {
                        episodeId = firstPlayable.id;
                        lastWatchedSeason = firstPlayable.seasonNumber;
                    }
                }
            }
            if (movie.status === MovieStatusEnum.CS ||
                !((_a = movie.episodes) === null || _a === void 0 ? void 0 : _a.length) ||
                !hasPlayableEpisodes(movie.episodes)) {
                showError(movie.movieType === "series"
                    ? t("coming_soon_modal.series")
                    : t("coming_soon_modal.movie"), "/home", t("layout.back_home"));
                return;
            }
            if (!episodeId) {
                showError(t("player.episode_not_found"), "/home", t("layout.back_home"));
                return;
            }
            if (movie.type === "EST" && !movie.bought) {
                showError(t("buy_movie_modal.title"), "/home", t("layout.back_home"));
                return;
            }
            return getEpisode(episodeId, profile.id).then(function (data) {
                if (!data) {
                    showError(t("player.episode_not_found"), "/home", t("layout.back_home"));
                    return;
                }
                if (data.episode.watchHistory) {
                    watchHistory = __assign(__assign({}, watchHistory), data.episode.watchHistory);
                }
                // The stored quality is a single row shared across every platform
                // (web, mobile, AndroidTV) and is not validated server-side, so it can
                // arrive null or as a label Silk has no mapper for (e.g. "1080p").
                // Instead of blocking playback, fall back to whichever quality Silk
                // can actually play — losing the saved quality beats not watching.
                var resolvedQuality = (watchHistory.quality || "HD").toUpperCase();
                var source = generateSource(data.episode.mappers, resolvedQuality);
                if (!source) {
                    for (var _i = 0, _a = ["HD", "SD"]; _i < _a.length; _i++) {
                        var fallbackQuality = _a[_i];
                        source = generateSource(data.episode.mappers, fallbackQuality);
                        if (source) {
                            resolvedQuality = fallbackQuality;
                            break;
                        }
                    }
                }
                if (!source) {
                    showError(t("player.episode_not_found"), "/home", t("layout.back_home"));
                    return;
                }
                watchHistory.quality = resolvedQuality;
                var seasons = [];
                var episodeDetails = null;
                if (movie.movieType === "series") {
                    seasons = movie.episodes
                        ? movie.episodes
                            .map(function (e) { return e.seasonNumber; })
                            .filter(function (v, i, a) { return a.indexOf(v) === i; })
                        : [];
                    episodeDetails = getEpisodeDetails(movie, episodeId);
                }
                var newWatchData = {
                    quality: currentQuality || watchHistory.quality || "HD",
                    language: watchHistory.language || "ka",
                    caption: watchHistory.caption || "off",
                    progress: watchHistory.progress || 0,
                    currentTime: watchHistory.currentTime || 0,
                    movieId: movie.id,
                    movie: movie,
                    episodeId: data.episode.id,
                    episodeDetails: episodeDetails,
                    source: source,
                    watchSessionId: data.watchSessionId,
                    seasons: seasons,
                    lastWatchedSeason: lastWatchedSeason,
                    profileId: profile.id,
                    mappers: data.episode.mappers,
                };
                setWatchData(newWatchData);
                setLoading(false);
            });
        })
            .catch(function (error) {
            console.error("Failed to load movie/episode:", error);
            showError(t("player.episode_not_found"), "/home", t("layout.back_home"));
        });
    }, [params.slug, params.episode, params.quality, profile.id]);
    var getEpisodeDetails = function (movie, episodeId) {
        if (!movie || !episodeId || !movie.episodes)
            return null;
        var episode = movie.episodes.filter(function (e) { return e.id === episodeId; })[0];
        return episode
            ? {
                episodeNumber: "S" + episode.seasonNumber + ":E" + episode.episodeNumber,
                episodeName: episode.name,
            }
            : null;
    };
    var handleBackToHome = function () {
        if (errorState.returnRoute === "back" || !errorState.returnRoute) {
            focusManager.popLayer();
            goBack();
        }
        else {
            navigate(errorState.returnRoute);
        }
    };
    if (errorActions.hasMessage()) {
        return React.createElement(InfoModal, { onClose: handleBackToHome, text: errorState.message });
    }
    if (loading || !shakaReady) {
        return React.createElement(LoadingSpinner, null);
    }
    return React.createElement(Player, { watchData: watchData, lastInputAtRef: lastInputAtRef });
};
export var WatchScreen = function () {
    return (React.createElement(PlayerStateProvider, null,
        React.createElement(PlayerErrorMessageProvider, null,
            React.createElement(WatchScreenInner, null))));
};



//////////////////
// WEBPACK FOOTER
// ./src/screens/Player/WatchScreen.tsx
// module id = 175
// module chunks = 1